import React, { useState, useRef, useEffect } from 'react';
import { Room, DashboardWidget as DashboardWidgetType, DashboardWidgetSize, DashboardWidgetMode } from '../types';
import { Settings, X, Move, MousePointer, MousePointerClick, Palette, Maximize2, RotateCw, Eye, Zap, Sparkles } from 'lucide-react';
import { SIZE_PRESETS, GRID_SIZE, MIN_SIZE } from '../store/dashboardStore';

interface DashboardWidgetProps {
  widget: DashboardWidgetType;
  room: Room;
  onMove: (position: { x: number; y: number }) => void;
  onResize: (size: DashboardWidgetSize) => void;
  onModeChange: (mode: DashboardWidgetMode) => void;
  onRemove: () => void;
  onStateChange?: (stateId: string) => void;
  onToggleStateChanges: () => void;
  canChangeState: boolean;
  globalStateChanges: boolean;
}

type ResizeDirection = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

const DashboardWidget: React.FC<DashboardWidgetProps> = ({
  widget,
  room,
  onMove,
  onResize,
  onModeChange,
  onRemove,
  onStateChange,
  onToggleStateChanges,
  canChangeState,
  globalStateChanges
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showStateOptions, setShowStateOptions] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isHovering, setIsHovering] = useState(false);
  const [resizeDirection, setResizeDirection] = useState<ResizeDirection | null>(null);
  const [initialSize, setInitialSize] = useState<DashboardWidgetSize>({ width: 0, height: 0 });
  const [initialPosition, setInitialPosition] = useState({ x: 0, y: 0 });
  const [initialMousePos, setInitialMousePos] = useState({ x: 0, y: 0 });
  const [activeTab, setActiveTab] = useState<'appearance' | 'behavior' | 'advanced'>('appearance');
  const widgetRef = useRef<HTMLDivElement>(null);

  const stateChangesEnabled = globalStateChanges && widget.enableStateChanges && canChangeState;

  // Snap to grid function
  const snapToGrid = (value: number): number => {
    return Math.round(value / GRID_SIZE) * GRID_SIZE;
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    const isButton = target.tagName === 'BUTTON' || target.closest('button');
    const isDragHandle = target.classList.contains('drag-handle') || target.closest('.drag-handle');
    const isWidgetContent = target.classList.contains('widget-content') || target.closest('.widget-content');
    const isResizeHandle = target.classList.contains('resize-handle') || target.closest('.resize-handle');
    
    if (isButton && !isDragHandle) return;
    if (isResizeHandle) return;
    if (!isDragHandle && !isWidgetContent) return;
    
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    
    const rect = widgetRef.current?.getBoundingClientRect();
    if (rect) {
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
    }
  };

  const handleResizeMouseDown = (e: React.MouseEvent, direction: ResizeDirection) => {
    e.preventDefault();
    e.stopPropagation();
    
    setIsResizing(true);
    setResizeDirection(direction);
    setInitialMousePos({ x: e.clientX, y: e.clientY });
    setInitialSize(widget.size);
    setInitialPosition(widget.position);
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging && !isResizing && widgetRef.current) {
        const container = widgetRef.current.parentElement;
        if (!container) return;
        
        const containerRect = container.getBoundingClientRect();
        const newX = Math.max(0, Math.min(
          e.clientX - containerRect.left - dragOffset.x,
          containerRect.width - widget.size.width
        ));
        const newY = Math.max(0, Math.min(
          e.clientY - containerRect.top - dragOffset.y,
          containerRect.height - widget.size.height
        ));
        
        onMove({ x: newX, y: newY });
      } else if (isResizing && resizeDirection) {
        const deltaX = e.clientX - initialMousePos.x;
        const deltaY = e.clientY - initialMousePos.y;
        
        let newWidth = initialSize.width;
        let newHeight = initialSize.height;
        let newX = initialPosition.x;
        let newY = initialPosition.y;
        
        // Calculate new dimensions and position based on resize direction
        switch (resizeDirection) {
          case 'n': // North (top edge)
            newHeight = Math.max(MIN_SIZE.height, initialSize.height - deltaY);
            newY = initialPosition.y + (initialSize.height - newHeight);
            break;
          case 's': // South (bottom edge)
            newHeight = Math.max(MIN_SIZE.height, initialSize.height + deltaY);
            break;
          case 'e': // East (right edge)
            newWidth = Math.max(MIN_SIZE.width, initialSize.width + deltaX);
            break;
          case 'w': // West (left edge)
            newWidth = Math.max(MIN_SIZE.width, initialSize.width - deltaX);
            newX = initialPosition.x + (initialSize.width - newWidth);
            break;
          case 'ne': // Northeast (top-right corner)
            newWidth = Math.max(MIN_SIZE.width, initialSize.width + deltaX);
            newHeight = Math.max(MIN_SIZE.height, initialSize.height - deltaY);
            newY = initialPosition.y + (initialSize.height - newHeight);
            break;
          case 'nw': // Northwest (top-left corner)
            newWidth = Math.max(MIN_SIZE.width, initialSize.width - deltaX);
            newHeight = Math.max(MIN_SIZE.height, initialSize.height - deltaY);
            newX = initialPosition.x + (initialSize.width - newWidth);
            newY = initialPosition.y + (initialSize.height - newHeight);
            break;
          case 'se': // Southeast (bottom-right corner)
            newWidth = Math.max(MIN_SIZE.width, initialSize.width + deltaX);
            newHeight = Math.max(MIN_SIZE.height, initialSize.height + deltaY);
            break;
          case 'sw': // Southwest (bottom-left corner)
            newWidth = Math.max(MIN_SIZE.width, initialSize.width - deltaX);
            newHeight = Math.max(MIN_SIZE.height, initialSize.height + deltaY);
            newX = initialPosition.x + (initialSize.width - newWidth);
            break;
        }
        
        // Snap to grid
        newWidth = snapToGrid(newWidth);
        newHeight = snapToGrid(newHeight);
        newX = snapToGrid(newX);
        newY = snapToGrid(newY);
        
        // Update size and position
        onResize({ width: newWidth, height: newHeight });
        if (newX !== widget.position.x || newY !== widget.position.y) {
          onMove({ x: newX, y: newY });
        }
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
      setResizeDirection(null);
    };

    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = isDragging ? 'grabbing' : (isResizing ? `${resizeDirection}-resize` : '');
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging, isResizing, dragOffset, onMove, widget.size, widget.position, resizeDirection, initialMousePos, initialSize, initialPosition, onResize]);

  const handleStateClick = (stateId: string) => {
    if (stateChangesEnabled && onStateChange) {
      onStateChange(stateId);
      setShowStateOptions(false);
    }
  };

  const handleWidgetHover = (hovering: boolean) => {
    setIsHovering(hovering);
    if (stateChangesEnabled && !isDragging && !isResizing) {
      setShowStateOptions(hovering);
    }
  };

  const handlePresetSizeChange = (presetKey: string) => {
    const preset = SIZE_PRESETS[presetKey as keyof typeof SIZE_PRESETS];
    if (preset) {
      onResize(preset);
    }
  };

  const renderContent = () => {
    const { mode } = widget;
    const { currentState } = room;

    const baseClasses = `widget-content w-full h-full rounded-lg transition-all duration-200 ${
      stateChangesEnabled ? 'cursor-pointer hover:scale-105' : (isDragging || isResizing) ? 'cursor-grabbing' : 'cursor-grab'
    }`;

    const handleContentClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (stateChangesEnabled && !isDragging && !isResizing) {
        setShowStateOptions(!showStateOptions);
      }
    };

    switch (mode) {
      case 'color-only':
        return (
          <div 
            className={baseClasses}
            style={{ backgroundColor: currentState.color }}
            onClick={handleContentClick}
            onMouseDown={handleMouseDown}
          />
        );

      case 'color-text':
        return (
          <div 
            className={`${baseClasses} flex items-center justify-center`}
            style={{ backgroundColor: currentState.color }}
            onClick={handleContentClick}
            onMouseDown={handleMouseDown}
          >
            <span className="text-white font-semibold text-center px-2 text-sm pointer-events-none">
              {currentState.name}
            </span>
          </div>
        );

      case 'color-text-title':
        return (
          <div 
            className={`${baseClasses} flex flex-col`}
            style={{ backgroundColor: currentState.color }}
            onClick={handleContentClick}
            onMouseDown={handleMouseDown}
          >
            <div className="flex-1 flex items-center justify-center pointer-events-none">
              <span className="text-white font-semibold text-center px-2 text-sm">
                {currentState.name}
              </span>
            </div>
            <div className="bg-black bg-opacity-20 p-2 rounded-b-lg pointer-events-none">
              <h3 className="text-white text-xs font-medium text-center truncate">
                {room.name}
              </h3>
            </div>
          </div>
        );

      case 'emoji-only':
        return (
          <div 
            className={`${baseClasses} flex items-center justify-center bg-white dark:bg-slate-800 border-2 border-slate-200 dark:border-slate-600`}
            onClick={handleContentClick}
            onMouseDown={handleMouseDown}
          >
            <span className="text-4xl pointer-events-none">
              {currentState.emoji || '⚪'}
            </span>
          </div>
        );

      case 'emoji-text':
        return (
          <div 
            className={`${baseClasses} flex items-center justify-center gap-2 bg-white dark:bg-slate-800 border-2 border-slate-200 dark:border-slate-600`}
            onClick={handleContentClick}
            onMouseDown={handleMouseDown}
          >
            <span className="text-2xl pointer-events-none">
              {currentState.emoji || '⚪'}
            </span>
            <span className="text-slate-800 dark:text-slate-200 font-semibold text-center text-sm pointer-events-none">
              {currentState.name}
            </span>
          </div>
        );

      case 'emoji-text-title':
        return (
          <div 
            className={`${baseClasses} flex flex-col bg-white dark:bg-slate-800 border-2 border-slate-200 dark:border-slate-600`}
            onClick={handleContentClick}
            onMouseDown={handleMouseDown}
          >
            <div className="flex-1 flex items-center justify-center gap-2 pointer-events-none">
              <span className="text-2xl">
                {currentState.emoji || '⚪'}
              </span>
              <span className="text-slate-800 dark:text-slate-200 font-semibold text-center text-sm">
                {currentState.name}
              </span>
            </div>
            <div className="bg-slate-100 dark:bg-slate-700 p-2 rounded-b-lg pointer-events-none">
              <h3 className="text-slate-800 dark:text-slate-200 text-xs font-medium text-center truncate">
                {room.name}
              </h3>
            </div>
          </div>
        );

      case 'emoji-color':
        return (
          <div 
            className={`${baseClasses} flex items-center justify-center`}
            style={{ backgroundColor: currentState.color }}
            onClick={handleContentClick}
            onMouseDown={handleMouseDown}
          >
            <span className="text-4xl pointer-events-none drop-shadow-lg">
              {currentState.emoji || '⚪'}
            </span>
          </div>
        );

      case 'emoji-color-text':
        return (
          <div 
            className={`${baseClasses} flex items-center justify-center gap-2`}
            style={{ backgroundColor: currentState.color }}
            onClick={handleContentClick}
            onMouseDown={handleMouseDown}
          >
            <span className="text-2xl pointer-events-none drop-shadow-lg">
              {currentState.emoji || '⚪'}
            </span>
            <span className="text-white font-semibold text-center text-sm pointer-events-none drop-shadow-lg">
              {currentState.name}
            </span>
          </div>
        );

      case 'emoji-color-text-title':
        return (
          <div 
            className={`${baseClasses} flex flex-col`}
            style={{ backgroundColor: currentState.color }}
            onClick={handleContentClick}
            onMouseDown={handleMouseDown}
          >
            <div className="flex-1 flex items-center justify-center gap-2 pointer-events-none">
              <span className="text-2xl drop-shadow-lg">
                {currentState.emoji || '⚪'}
              </span>
              <span className="text-white font-semibold text-center text-sm drop-shadow-lg">
                {currentState.name}
              </span>
            </div>
            <div className="bg-black bg-opacity-20 p-2 rounded-b-lg pointer-events-none">
              <h3 className="text-white text-xs font-medium text-center truncate">
                {room.name}
              </h3>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div
      ref={widgetRef}
      className={`absolute select-none group ${(isDragging || isResizing) ? 'z-50' : 'z-10'}`}
      style={{
        left: widget.position.x,
        top: widget.position.y,
        width: widget.size.width,
        height: widget.size.height,
      }}
      onMouseEnter={() => handleWidgetHover(true)}
      onMouseLeave={() => handleWidgetHover(false)}
    >
      {/* Widget Content */}
      <div className="relative w-full h-full">
        {renderContent()}
        
        {/* State Change Indicator */}
        {stateChangesEnabled && isHovering && !isDragging && !isResizing && (
          <div className="absolute top-1 left-1/2 transform -translate-x-1/2 transition-opacity pointer-events-none">
            <MousePointerClick className="w-4 h-4 text-white drop-shadow-lg" />
          </div>
        )}
        
        {/* Drag Handle */}
        <div 
          className="drag-handle absolute top-1 left-1 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab hover:cursor-grab active:cursor-grabbing p-1"
          onMouseDown={handleMouseDown}
        >
          <Move className="w-4 h-4 text-white drop-shadow-lg pointer-events-none" />
        </div>
        
        {/* Settings Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            setShowSettings(!showSettings);
          }}
          className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity p-1 bg-black bg-opacity-50 rounded hover:bg-opacity-70 z-10"
        >
          <Settings className="w-3 h-3 text-white" />
        </button>
        
        {/* Remove Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="absolute top-1 right-8 opacity-0 group-hover:opacity-100 transition-opacity p-1 bg-red-500 bg-opacity-80 rounded hover:bg-opacity-100 z-10"
        >
          <X className="w-3 h-3 text-white" />
        </button>

        {/* Resize Handles */}
        <div className="opacity-0 group-hover:opacity-100 transition-opacity">
          {/* Corner Handles */}
          <div
            className="resize-handle absolute top-0 left-0 w-3 h-3 cursor-nw-resize z-20"
            onMouseDown={(e) => handleResizeMouseDown(e, 'nw')}
          >
            <div className="absolute top-0 left-0 w-2 h-2 bg-white border border-gray-400 rounded-br-sm shadow-sm"></div>
          </div>
          
          <div
            className="resize-handle absolute top-0 right-0 w-3 h-3 cursor-ne-resize z-20"
            onMouseDown={(e) => handleResizeMouseDown(e, 'ne')}
          >
            <div className="absolute top-0 right-0 w-2 h-2 bg-white border border-gray-400 rounded-bl-sm shadow-sm"></div>
          </div>
          
          <div
            className="resize-handle absolute bottom-0 left-0 w-3 h-3 cursor-sw-resize z-20"
            onMouseDown={(e) => handleResizeMouseDown(e, 'sw')}
          >
            <div className="absolute bottom-0 left-0 w-2 h-2 bg-white border border-gray-400 rounded-tr-sm shadow-sm"></div>
          </div>
          
          <div
            className="resize-handle absolute bottom-0 right-0 w-3 h-3 cursor-se-resize z-20"
            onMouseDown={(e) => handleResizeMouseDown(e, 'se')}
          >
            <div className="absolute bottom-0 right-0 w-2 h-2 bg-white border border-gray-400 rounded-tl-sm shadow-sm"></div>
          </div>

          {/* Edge Handles */}
          <div
            className="resize-handle absolute top-0 left-1/2 transform -translate-x-1/2 w-6 h-2 cursor-n-resize z-20"
            onMouseDown={(e) => handleResizeMouseDown(e, 'n')}
          >
            <div className="w-full h-1 bg-white border border-gray-400 rounded-b shadow-sm"></div>
          </div>
          
          <div
            className="resize-handle absolute bottom-0 left-1/2 transform -translate-x-1/2 w-6 h-2 cursor-s-resize z-20"
            onMouseDown={(e) => handleResizeMouseDown(e, 's')}
          >
            <div className="w-full h-1 bg-white border border-gray-400 rounded-t shadow-sm"></div>
          </div>
          
          <div
            className="resize-handle absolute left-0 top-1/2 transform -translate-y-1/2 w-2 h-6 cursor-w-resize z-20"
            onMouseDown={(e) => handleResizeMouseDown(e, 'w')}
          >
            <div className="w-1 h-full bg-white border border-gray-400 rounded-r shadow-sm"></div>
          </div>
          
          <div
            className="resize-handle absolute right-0 top-1/2 transform -translate-y-1/2 w-2 h-6 cursor-e-resize z-20"
            onMouseDown={(e) => handleResizeMouseDown(e, 'e')}
          >
            <div className="w-1 h-full bg-white border border-gray-400 rounded-l shadow-sm"></div>
          </div>
        </div>
      </div>

      {/* State Options Panel */}
      {showStateOptions && stateChangesEnabled && !isDragging && !isResizing && (
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-slate-200 dark:border-slate-700 p-2 z-50 min-w-32">
          <div className="text-xs font-medium text-slate-700 dark:text-slate-300 mb-2 text-center">Change State</div>
          <div className="space-y-1">
            {room.states.map(state => (
              <button
                key={state.id}
                onClick={(e) => {
                  e.stopPropagation();
                  handleStateClick(state.id);
                }}
                className={`w-full text-left px-2 py-1.5 text-xs rounded transition-colors flex items-center gap-2 ${
                  state.id === room.currentState.id
                    ? 'bg-slate-100 dark:bg-slate-700 text-slate-900 dark:text-slate-100 font-medium'
                    : 'hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300'
                }`}
              >
                {state.emoji && (
                  <span className="text-sm">{state.emoji}</span>
                )}
                <div
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: state.color }}
                />
                <span className="truncate">{state.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Enhanced Settings Panel */}
      {showSettings && (
        <div className="absolute top-full left-0 mt-2 bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 z-50 min-w-80 max-w-sm">
          {/* Header */}
          <div className="p-4 border-b border-slate-200 dark:border-slate-700">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-slate-800 dark:text-slate-200 flex items-center gap-2">
                <Palette className="w-4 h-4 text-blue-500" />
                Widget Settings
              </h3>
              <button
                onClick={() => setShowSettings(false)}
                className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-colors"
              >
                <X className="w-4 h-4 text-slate-500 dark:text-slate-400" />
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-slate-200 dark:border-slate-700">
            {(['appearance', 'behavior', 'advanced'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex-1 px-3 py-2 text-xs font-medium transition-colors ${
                  activeTab === tab
                    ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400 bg-blue-50/50 dark:bg-blue-900/20'
                    : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="p-4 max-h-96 overflow-y-auto">
            {activeTab === 'appearance' && (
              <div className="space-y-4">
                {/* Display Mode */}
                <div>
                  <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-2 flex items-center gap-2">
                    <Eye className="w-3 h-3" />
                    Display Mode
                  </label>
                  <select
                    value={widget.mode}
                    onChange={(e) => onModeChange(e.target.value as DashboardWidgetMode)}
                    className="w-full text-xs px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                  >
                    <optgroup label="Color Modes">
                      <option value="color-only">🎨 Color Only</option>
                      <option value="color-text">🎨 Color + Text</option>
                      <option value="color-text-title">🎨 Color + Text + Title</option>
                    </optgroup>
                    <optgroup label="Emoji Modes">
                      <option value="emoji-only">😀 Emoji Only</option>
                      <option value="emoji-text">😀 Emoji + Text</option>
                      <option value="emoji-text-title">😀 Emoji + Text + Title</option>
                    </optgroup>
                    <optgroup label="Emoji + Color Modes">
                      <option value="emoji-color">🌈 Emoji + Color</option>
                      <option value="emoji-color-text">🌈 Emoji + Color + Text</option>
                      <option value="emoji-color-text-title">🌈 Emoji + Color + Text + Title</option>
                    </optgroup>
                  </select>
                </div>

                {/* Quick Size Presets */}
                <div>
                  <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-2 flex items-center gap-2">
                    <Maximize2 className="w-3 h-3" />
                    Quick Sizes
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(SIZE_PRESETS).map(([key, size]) => (
                      <button
                        key={key}
                        onClick={() => handlePresetSizeChange(key)}
                        className={`px-3 py-2 border rounded-lg text-xs transition-all duration-200 ${
                          widget.size.width === size.width && widget.size.height === size.height
                            ? 'bg-blue-500 border-blue-500 text-white shadow-lg transform scale-105'
                            : 'border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 hover:border-blue-300 dark:hover:border-blue-600'
                        }`}
                      >
                        <div className="font-medium">{key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}</div>
                        <div className="text-xs opacity-75">{size.width}×{size.height}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Custom Dimensions */}
                <div>
                  <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-2 flex items-center gap-2">
                    <RotateCw className="w-3 h-3" />
                    Custom Dimensions
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-slate-600 dark:text-slate-400 mb-1 block">Width (px)</label>
                      <input
                        type="number"
                        value={widget.size.width}
                        onChange={(e) => onResize({ ...widget.size, width: Math.max(MIN_SIZE.width, parseInt(e.target.value) || MIN_SIZE.width) })}
                        min={MIN_SIZE.width}
                        step={GRID_SIZE}
                        className="w-full text-xs px-2 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-slate-600 dark:text-slate-400 mb-1 block">Height (px)</label>
                      <input
                        type="number"
                        value={widget.size.height}
                        onChange={(e) => onResize({ ...widget.size, height: Math.max(MIN_SIZE.height, parseInt(e.target.value) || MIN_SIZE.height) })}
                        min={MIN_SIZE.height}
                        step={GRID_SIZE}
                        className="w-full text-xs px-2 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-2 flex items-center gap-1">
                    <Sparkles className="w-3 h-3" />
                    Drag edges/corners for visual resizing
                  </p>
                </div>
              </div>
            )}

            {activeTab === 'behavior' && (
              <div className="space-y-4">
                {/* Interactive State Changes */}
                {canChangeState && (
                  <div className="p-3 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
                    <label className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={widget.enableStateChanges}
                        onChange={onToggleStateChanges}
                        className="rounded border-slate-300 dark:border-slate-600 text-blue-500 focus:ring-blue-500 dark:focus:ring-blue-400"
                      />
                      <div>
                        <span className="text-sm font-medium text-slate-700 dark:text-slate-300 flex items-center gap-2">
                          <MousePointerClick className="w-4 h-4 text-blue-500" />
                          Enable State Changes
                        </span>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                          Allow clicking this widget to change the room state
                        </p>
                      </div>
                    </label>
                  </div>
                )}

                {/* Widget Status */}
                <div className="space-y-3">
                  <h4 className="text-xs font-medium text-slate-700 dark:text-slate-300 flex items-center gap-2">
                    <Zap className="w-3 h-3" />
                    Widget Status
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 bg-slate-50 dark:bg-slate-700 rounded-lg">
                      <div className="text-xs text-slate-500 dark:text-slate-400">Position</div>
                      <div className="text-sm font-medium text-slate-700 dark:text-slate-300">
                        {widget.position.x}, {widget.position.y}
                      </div>
                    </div>
                    <div className="p-3 bg-slate-50 dark:bg-slate-700 rounded-lg">
                      <div className="text-xs text-slate-500 dark:text-slate-400">Size</div>
                      <div className="text-sm font-medium text-slate-700 dark:text-slate-300">
                        {widget.size.width}×{widget.size.height}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Room Information */}
                <div className="p-3 bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-700 dark:to-slate-800 rounded-lg border border-slate-200 dark:border-slate-600">
                  <h4 className="text-xs font-medium text-slate-700 dark:text-slate-300 mb-2">Connected Room</h4>
                  <div className="flex items-center gap-2 mb-2">
                    {room.currentState.emoji && (
                      <span className="text-lg">{room.currentState.emoji}</span>
                    )}
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: room.currentState.color }}
                    />
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{room.name}</span>
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">
                    Current: {room.currentState.name}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'advanced' && (
              <div className="space-y-4">
                {/* Widget ID */}
                <div>
                  <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-2">Widget ID</label>
                  <div className="p-2 bg-slate-50 dark:bg-slate-700 rounded border text-xs font-mono text-slate-600 dark:text-slate-400">
                    {widget.id}
                  </div>
                </div>

                {/* Room ID */}
                <div>
                  <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-2">Room ID</label>
                  <div className="p-2 bg-slate-50 dark:bg-slate-700 rounded border text-xs font-mono text-slate-600 dark:text-slate-400">
                    {widget.roomId}
                  </div>
                </div>

                {/* Grid Information */}
                <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-700">
                  <h4 className="text-xs font-medium text-amber-800 dark:text-amber-300 mb-2">Grid System</h4>
                  <div className="text-xs text-amber-700 dark:text-amber-400 space-y-1">
                    <div>Grid Size: {GRID_SIZE}px</div>
                    <div>Min Size: {MIN_SIZE.width}×{MIN_SIZE.height}px</div>
                    <div>Snap to Grid: Enabled</div>
                  </div>
                </div>

                {/* Reset Position */}
                <button
                  onClick={() => onMove({ x: 20, y: 20 })}
                  className="w-full px-3 py-2 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors text-xs font-medium"
                >
                  Reset to Origin (20, 20)
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardWidget;